import { JSX, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import party from 'party-js';
import { TicketIcon } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import MainView from '../../components/main-view/main-view';
import { usePlanStore, useServiceStore } from '../../store';
import Card from '../../components/card/card';
import Button from '../../components/button/button';
import { Beneficiary } from '../../types/beneficiary.type';
import { mapCurrencyToSymbol } from '../../utils/currency.util';
import PaymentForm from '../../components/payment-form/payment-form';
import { PaymentFormData } from '../../types/payment-form-data.type';
import SuccessfulIcon from '../../assets/images/successful.svg';
import BeneficiariesCard from '../../components/beneficiaries-card/beneficiaries-card';
import { Plan } from '../../types/plan.type';
import { PaymentMethod } from '../../types/payment-config.type';
import { MobilePaymentData } from '../../components/method-forms/mobile-payment-form/mobile-payment-form';
import ResponseCodes from '../../constants/response-codes.constant';
import PaymentErrorModal from '../../components/payment-error-modal/payment-error-modal';
import { Analytics } from '../../utils/analytics.util';
import { User } from '../../types/user.type';
import { userSDK } from '../../webservices/user.webservice';
import { serviceSDK } from '../../webservices/service.webservice';
import { mapResponseError } from '../../utils/error-mapper.util';
import { usePaymentStore } from '../../stores/payment-store';
import { usePendingPaymentStore } from '../../stores/pending-payment-store';
import ConfirmDirectDebitOTPView from '../confirm-direct-debit-otp/confirm-direct-debit-otp.view';

import styles from './plan.module.scss';
import PlansCard from './components/plans-card';
import PaymentMethodsCard from './components/payment-methods-card';

const PlanView = () => {
    const [params] = useSearchParams({});
    const confettiImgRef = useRef<HTMLImageElement>(null);
    const navigate = useNavigate();

    const { services, fetch: fetchServices } = useServiceStore();
    const planState = usePlanStore();
    const { methods, banks, currencies } = usePaymentStore();
    const { setPendingPayment, step1Payload, clearPendingPayment } =
        usePendingPaymentStore();

    const [currentStep, setCurrentStep] = useState(1);
    const [pendingOTPConfirmation, setPendingOTPConfirmation] = useState(false);
    const [mobileInitialValues, setMobileInitialValues] = useState<
        Partial<MobilePaymentData> | undefined
    >(undefined);
    const [paymentFormKey, setPaymentFormKey] = useState(0);
    const [errorModal, setErrorModal] = useState({
        shown: false,
        message: '',
    });
    const [knownUser, setKnownUser] = useState<User>();
    const [discount, setDiscount] = useState(0);
    const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>();
    const [paymentDone, setPaymentDone] = useState(false);

    useEffect(() => {
        Analytics.track('PageVisited', { action: 'PageLoaded' });
        fetchServices();
    }, []);

    useEffect(() => {
        manageQueryParams(params);
    }, [params, services]);

    useEffect(() => {
        confettiImgRef.current &&
            paymentDone &&
            party.confetti(confettiImgRef.current, { count: 100 });
    }, [paymentDone]);

    const manageQueryParams = (params: URLSearchParams) => {
        const userId = params.has('userid')
            ? Number(params.get('userid'))
            : undefined;
        const planId = params.has('planid')
            ? Number(params.get('planid'))
            : undefined;

        userId && fetchUserData(userId);
        if (planId) {
            let selectedPlan;
            for (const service of services) {
                for (const plan of service.plans!) {
                    if (plan.id === planId) {
                        selectedPlan = { ...plan, serviceCode: service.code };
                        break;
                    }
                }
            }
            selectedPlan && selectPlan(selectedPlan);
        }
    };

    const selectPlan = (plan: Plan) => {
        const { price, currency } = plan.prices[0];
        planState.selectPlan(plan);
        planState.setPricing({ price, currency });
        setCurrentStep(currentStep + 1);
        Analytics.track('PlanSelected', {
            action: 'ClickOnItem',
            plan: { id: plan.id, name: (plan as any).name },
        });
    };

    const fetchUserData = async (userId: number) => {
        const response = await userSDK.getById(userId);
        if (response.code !== ResponseCodes.PROCESS_OK) {
            return console.error('[!] Error getting user:', response);
        }
        setKnownUser(response.data);
    };

    const addBeneficiaries = (beneficiaries: Beneficiary[]) => {
        planState.addBeneficiaries(beneficiaries);
        setCurrentStep(currentStep + 1);

        const beneficiary = beneficiaries[0];
        Analytics.track('BeneficiariesAdded', {
            action: 'ButtonPressed',
            user: {
                fullName: beneficiary.fullName,
                email: beneficiary.email,
            },
        });
    };

    const selectPaymentMethod = (methodCode: string) => {
        const method = methods.find((m) => m.code === methodCode);
        if (!method) return;
        setSelectedMethod(method);
        setCurrentStep(currentStep + 1);
        Analytics.track('PaymentMethodSelected', {
            action: 'ClickOnItem',
            paymentMethod: {
                name: method.displayName,
                currency: method.currency,
            },
        });
    };

    const submitPayment = async (payment: PaymentFormData) => {
        const buyRequest = {
            payment: {
                ...payment,
                methodId: selectedMethod!.id,
                couponCode: payment.couponCode,
            },
            beneficiaries: planState.beneficiaries,
            planId: planState.selectedPlan!.id,
        };

        const response = await serviceSDK.buySubscription(buyRequest);

        if (response.code === ResponseCodes.PENDING_PAYMENT_CONFIRMATION) {
            const userId = response.data?.subscriptions?.[0]?.userId;
            if (!userId) {
                return setErrorModal({
                    shown: true,
                    message:
                        'Error: no se pudo obtener el ID de usuario del pago pendiente.',
                });
            }
            setPendingPayment({
                paymentId: response.data.payment.id,
                intentId: response.data.payment.intentId,
                userId,
                step1Payload: payment,
            });
            setPendingOTPConfirmation(true);
            Analytics.track('PaymentSubmitted', {
                action: 'ButtonPressed',
                paymentAmount: payment.amount,
            });
            return;
        }

        if (response.code !== ResponseCodes.PROCESS_OK) {
            Analytics.track('ErrorSubmittingPayment', {
                action: 'ButtonPressed',
                error: response,
            });
            return setErrorModal({
                shown: true,
                message: mapResponseError(response),
            });
        }

        setPaymentDone(true);
        Analytics.track('PaymentSubmitted', {
            action: 'ButtonPressed',
            paymentAmount: payment.amount,
        });
    };

    const handleOTPRestart = () => {
        if (step1Payload) {
            setMobileInitialValues({
                bankId: step1Payload.bankId,
                date: step1Payload.claimedAt,
                referenceCode: step1Payload.referenceCode,
                isThirdPartyPayer: step1Payload.payer?.isThirdParty ?? false,
                phone: step1Payload.payer?.phoneNumber,
                nin: step1Payload.payer?.document,
                documentType: step1Payload.payer?.documentType,
                name: step1Payload.payer?.name,
            });
            setPaymentFormKey((k) => k + 1);
        }
        clearPendingPayment();
        setPendingOTPConfirmation(false);
    };

    const renderPaymentFormCard = (
        currentStep: number,
        paymentData: {
            price: number;
            method?: PaymentMethod;
            type: string;
        },
        shown: boolean
    ): JSX.Element => {
        const currency = currencies.find(
            (currency) => currency.currency === paymentData.method?.currency
        );
        return (
            <Card
                className={classNames({
                    [styles.card]: true,
                    [styles.shown]: currentStep > 3 && shown,
                    [styles.paymentForm]: true,
                })}
            >
                <span
                    className={classNames({
                        [styles.cardTitle]: true,
                        [styles.active]: true,
                    })}
                >
                    Monto total:{' '}
                    {mapCurrencyToSymbol(currency?.currency ?? 'USD')}
                    {paymentData.price.withRate(currency?.rate ?? 1).toFixed(2)}
                </span>
                {!currency ? null : (
                    <PaymentForm
                        key={paymentFormKey}
                        method={paymentData.method!}
                        rate={currency.rate}
                        amount={paymentData.price}
                        currency={currency.currency}
                        type={paymentData.type}
                        onPaymentData={submitPayment}
                        onDiscount={setDiscount}
                        banks={banks}
                        mobileInitialValues={mobileInitialValues}
                    />
                )}
            </Card>
        );
    };

    const renderAllDoneCard = (shown: boolean): JSX.Element => {
        return (
            <Card
                className={classNames({
                    [styles.card]: true,
                    [styles.allDone]: true,
                    [styles.shown]: shown,
                })}
            >
                <h4>¡Felicidades!</h4>
                <img src={SuccessfulIcon} ref={confettiImgRef} />
                {selectedMethod?.code === 'cc' ? (
                    <p>
                        Ya estás suscrito a PANA y hemos enviado toda la
                        información de tu suscripción a tu correo electrónico
                        🙌🏻.
                    </p>
                ) : (
                    <p>
                        Hemos registrado tu pago existosamente y te haremos
                        saber vía correo electrónico cuando tu suscripción esté
                        activa 🙌🏻
                    </p>
                )}
                <Button
                    text={'Finalizar'}
                    onClick={() =>
                        window.location.assign('https://home.panatech.io')
                    }
                />
            </Card>
        );
    };

    const goToRedeemGiftCard = () => navigate('/redeem-gift-card');

    return (
        <MainView>
            <div className={styles.container}>
                <aside>
                    <h4>Casi todo listo para tener tu suscripción PANA</h4>
                    <span>
                        Coméntanos sobre ti para preparar tu suscripción a la
                        medida.
                    </span>
                </aside>
                <section>
                    <div className={styles.cardsContainer}>
                        <PlansCard
                            services={services}
                            pricingType={planState.purchaseType}
                            currentStep={currentStep}
                            shown={!paymentDone}
                            onPlan={selectPlan}
                            onStep={setCurrentStep}
                        />
                        <BeneficiariesCard
                            plan={planState.selectedPlan}
                            currentStep={currentStep}
                            shown={currentStep > 1 && !paymentDone}
                            rollUp={currentStep > 2}
                            submitBeneficiaries={addBeneficiaries}
                            goBack={() => setCurrentStep(2)}
                            knownUser={knownUser}
                        />
                        <PaymentMethodsCard
                            currentStep={currentStep}
                            priceType={planState.purchaseType}
                            methods={methods}
                            shown={!paymentDone}
                            onStep={setCurrentStep}
                            onMethod={selectPaymentMethod}
                        />
                        {renderPaymentFormCard(
                            currentStep,
                            {
                                price: planState.price.withDiscount(discount),
                                method: selectedMethod,
                                type: planState.purchaseType,
                            },
                            !paymentDone && !pendingOTPConfirmation
                        )}
                        {pendingOTPConfirmation && (
                            <ConfirmDirectDebitOTPView
                                onBack={() => setPendingOTPConfirmation(false)}
                                onRestart={handleOTPRestart}
                                onSuccess={() => {
                                    setPendingOTPConfirmation(false);
                                    setPaymentDone(true);
                                }}
                            />
                        )}
                        {renderAllDoneCard(paymentDone)}
                    </div>
                    {currentStep === 1 && (
                        <Card className={styles.giftCardContainer}>
                            <h4>
                                ¿Tienes una Gift Card?{' '}
                                <TicketIcon size={22} color="#3e3e3e" />
                            </h4>
                            <span>Canjéala aquí y activa tu suscripción</span>
                            <Button
                                text="Activar"
                                onClick={goToRedeemGiftCard}
                            />
                        </Card>
                    )}
                </section>
                <PaymentErrorModal
                    shown={errorModal.shown}
                    message={errorModal.message}
                    onDismiss={() =>
                        setErrorModal({ shown: false, message: '' })
                    }
                />
            </div>
        </MainView>
    );
};

export default PlanView;
